// =============================================================================
// staticSite/types — modele dla migracji WP -> strona statyczna (Cloudflare Pages).
//
// SiteModel = wszystko, co panel wie o mapie, w ksztalcie gotowym do renderu.
// PageModel = jedna strona (artykul / pillar / autor / podstrona / kategoria / home).
// RenderedFile = plik wynikowy (sciezka + bajty). ParityReport = wynik bramek.
// =============================================================================

import type { SchemaGraph } from "@/services/schema/types";
import type { RgbColor } from "@/types/brand";
import type { DesignBrief } from "./designBrief";

export type AstroThemeId = "astropaper" | "brand";

export type PageKind =
  | "home"
  | "article"
  | "pillar"
  | "author"
  | "subpage"
  | "category"
  | "legacy_post"
  | "legacy_page"
  | "not_found";

export type StaticSiteStatus =
  | "draft"
  | "analyzed"
  | "previewed"
  | "deployed"
  | "live"
  | "rolled_back"
  | "failed";

export type ThemeId = "editorial" | "magazine" | "minimal" | "sidebar";

export interface StaticSiteSettings {
  /** Hosty linkow sprzedazowych (money links). Pusta lista = KAZDY host zewnetrzny traktowany jako money. */
  money_hosts: string[];
  posts_per_page?: number;
  /** Baza permalinkow kategorii WP (domyslnie "category"). */
  category_base?: string;
  /** Czy obrazy z hostow zewnetrznych (fal.media, storage Supabase) re-hostowac na stronie. */
  rehost_external_images?: boolean;
  /** Tekst pod nazwa strony (naglowek / meta home). */
  tagline?: string;
  /** Kiedy ostatnio wykryto ustawienia z WP (auto-detekcja przy pierwszym otwarciu). */
  detected_at?: string;
  /**
   * Silnik budowania (od 2026-09-22):
   *   browser — wlasny renderer w przegladarce (pageRenderer + themes/), domyslny w pilotazu
   *   astro   — kontener build-worker (Astro + gotowy motyw), przez /api/static-build/
   */
  build_backend?: BuildBackend;
  /** Motyw Astro (tylko dla build_backend = astro). */
  astro_theme?: AstroThemeId;
  /** System designu per domena (motyw brand): paleta, czcionki, motyw dekoracji, teksty hero. */
  design?: DesignBrief;
  /** Menu strony (glowne + stopka). Brak = assembler buduje z WP (nav) albo automatycznie. */
  menu?: SiteMenu;
}

// -----------------------------------------------------------------------------
// Menu strony (2026-09-23): kazda kategoria w menu, grupy = naglowki dropdownow
// BEZ URL-a. Kategorie zostaja plaskie (/category/x/). Max 2 poziomy.
// -----------------------------------------------------------------------------

type MenuItemType = "group" | "category" | "page" | "author" | "home" | "url";

export interface MenuItem {
  /** Stabilny id (DnD): `${type}:${ref|href|label}` */
  id: string;
  label: string;
  type: MenuItemType;
  /** category: slug; page: sciezka (/o-nas/); author/home: bez ref */
  ref?: string;
  /** Tylko type=url (absolutny albo /sciezka/) */
  href?: string;
  /** Tylko type=group */
  children?: MenuItem[];
}

export interface SiteMenu {
  main: MenuItem[];
  footer?: MenuItem[];
  source: "ai" | "wp" | "manual" | "auto";
  generated_at?: string;
}

export type BuildBackend = "browser" | "astro";

export const DEFAULT_SITE_SETTINGS: StaticSiteSettings = {
  money_hosts: [],
  posts_per_page: 10,
  category_base: "category",
  rehost_external_images: true,
};

/** Wiersz tabeli static_sites. */
export interface StaticSiteRow {
  id: string;
  user_id: string | null;
  website_id: string;
  map_id: string;
  workspace: "firma" | "sideprojects";
  domain: string;
  status: StaticSiteStatus;
  theme_id: ThemeId;
  theme_seed: string | null;
  settings: StaticSiteSettings;
  pages_cf_account_id: string | null;
  pages_cf_account_remote_id: string | null;
  pages_project_name: string | null;
  pages_subdomain: string | null;
  dns_cf_account_id: string | null;
  zone_id: string | null;
  dns_snapshot: unknown | null;
  custom_domain_status: string | null;
  cutover_at: string | null;
  rolled_back_at: string | null;
  needs_redeploy: boolean;
  asset_manifest: Record<string, AssetManifestEntry>;
  last_deploy_id: string | null;
  last_parity: ParityReport | null;
  analyzed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssetManifestEntry {
  hash: string;
  bytes: number;
  contentType: string;
  src: string;
}

export interface OutboundLink {
  href: string;
  host: string;
  anchor: string;
  rel: string;
  is_money: boolean;
}

export interface PageModel {
  kind: PageKind;
  /** Stabilny identyfikator (node_id / subpage id / slug kategorii / "home"). */
  id: string;
  sourceTable: string | null;
  sourceId: string | null;
  /** Sciezka na stronie statycznej: zawsze /.../ z trailing slash. */
  path: string;
  /** Aktualny URL na WP (parity) — null gdy sciezka pochodna ze sluga. */
  sourceUrl: string | null;
  /** Widoczny H1. */
  title: string;
  seoTitle: string | null;
  metaDescription: string | null;
  /** Tresc — VERBATIM z DB (renderer nie rusza <a>, zmienia tylko sciezki obrazow). */
  bodyHtml: string;
  excerpt: string | null;
  featuredImageUrl: string | null;
  datePublished: string | null;
  dateModified: string | null;
  categorySlug: string | null;
  categoryName: string | null;
  schemaGraph: SchemaGraph | null;
  schemaWarnings: string[];
  schemaErrors: string[];
}

export interface CategoryModel {
  slug: string;
  name: string;
  path: string;
  description: string | null;
  count: number;
}

export interface AuthorModel {
  name: string;
  slug: string;
  path: string | null;
  photoUrl: string | null;
  bioHtml: string | null;
  jobTitle: string | null;
  sameAs: string[];
}

export interface BrandModel {
  logoSvg: string | null;
  themeColor: string | null;
  colors: RgbColor[];
}

export interface SiteModel {
  mapId: string;
  websiteId: string;
  /** apex host, lowercase, bez www */
  domain: string;
  /** https://domain (bez trailing slash) */
  baseUrl: string;
  siteName: string;
  language: string;
  settings: StaticSiteSettings;
  pages: PageModel[];
  categories: CategoryModel[];
  author: AuthorModel | null;
  brand: BrandModel | null;
  /** Menu (zawsze wypelnione przez assembler po walidacji) — motywy renderuja TO, nie wlasne listy kategorii. */
  menu: SiteMenu;
  /** URL-e z live sitemap WP (inwentarz do bramki G3). Pusta lista = nie pobrano. */
  wpSitemapUrls: string[];
  /** Ostrzezenia z etapu skladania (np. brak sync wp_posts, brak profilu). */
  warnings: string[];
  assembledAt: string;
}

export interface RenderedFile {
  /** Sciezka w projekcie Pages, np. "/index.html", "/slug/index.html", "/_redirects". */
  path: string;
  bytes: Uint8Array;
  contentType: string;
  /** Dla plikow tekstowych — ta sama tresc jako string (podglad + parity bez dekodowania). */
  text?: string;
}

export interface AssetRef {
  /** Oryginalny URL (absolutny). */
  src: string;
  /** Docelowa sciezka na stronie statycznej. */
  path: string;
  /** Skad pochodzi: ta sama domena (wp-content/uploads) czy host zewnetrzny. */
  origin: "same_host" | "external";
  /** Ktore strony go uzywaja (do raportu). */
  usedBy: string[];
}

export type GateId = "G1" | "G2" | "G3" | "G4" | "G5" | "G6";

export interface GateResult {
  id: GateId;
  label: string;
  level: "hard" | "warn";
  passed: boolean;
  issues: string[];
}

export interface PageParity {
  path: string;
  kind: PageKind;
  title: string;
  sourceUrl: string | null;
  moneyLinks: number;
  outboundLinks: number;
  g1: boolean | null;
  g2: boolean | null;
  g4: boolean | null;
  g5: boolean | null;
  g6: boolean | null;
  issues: string[];
}

export interface MissingUrl {
  url: string;
  path: string;
  suggestedRedirect: string | null;
  reason: string;
}

export interface ParitySummary {
  blocking: boolean;
  pages: number;
  hardFailures: number;
  warnings: number;
  moneyLinksTotal: number;
  missingUrls: number;
  analyzedAt: string;
}

export interface ParityReport {
  gates: GateResult[];
  pages: PageParity[];
  missingUrls: MissingUrl[];
  blocking: boolean;
  summary: ParitySummary;
}

export interface StaticRedirectRow {
  id?: string;
  site_id?: string;
  from_path: string;
  to_path: string;
  code: 301 | 302 | 307 | 308 | 410;
  source: "auto_wp" | "wp_redirects" | "sitemap_gap" | "manual";
  reason: string | null;
}

export type MigrationPhase = "assemble" | "assets" | "render" | "parity" | "upload" | "deploy" | "dns";

export type MigrationProgressEvent =
  | { type: "phase"; phase: MigrationPhase; message: string }
  | { type: "progress"; phase: MigrationPhase; current: number; total: number; message?: string }
  | { type: "warning"; message: string }
  | { type: "done"; message: string }
  | { type: "error"; message: string };

export type ProgressCallback = (ev: MigrationProgressEvent) => void;
